import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  IconButton,
  Button,
  Chip,
  Divider,
  CircularProgress,
  Alert,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  Toolbar,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  FormLabel,
  FormControlLabel,
  RadioGroup,
  Radio,
  Checkbox,
  Tooltip,
  Snackbar,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Reply as ReplyIcon,
  Forward as ForwardIcon,
  Delete as DeleteIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  AttachFile as AttachFileIcon,
  Download as DownloadIcon,
  PersonAdd as PersonAddIcon,
  Person as PersonIcon,
  Search as SearchIcon,
  Lightbulb as LightbulbIcon,
  LinkOff as LinkOffIcon,
  Block as BlockIcon,
  Psychology as AiIcon,
  SaveAlt as SaveAltIcon,
} from '@mui/icons-material';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useParams, useNavigate } from 'react-router-dom';
import api, { emailsApi, customersApi, aiMailApi, customerDocumentsApi } from '../../services/api';
import { format } from 'date-fns';
import { it } from 'date-fns/locale';

// AI Components
import { AiSuggestionPanel, AiCategoryBadge } from '../../components/ai';
import { formatCustomerName } from '../../utils/formatCustomerName';

export default function EmailDetail() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  // Stati per dialog associazione cliente
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [suggestionsLoading, setSuggestionsLoading] = useState(false);
  const [customerSearch, setCustomerSearch] = useState('');
  const [customerOptions, setCustomerOptions] = useState([]);
  
  // Stati per dialog gestione email anagrafica
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [selectedCustomerData, setSelectedCustomerData] = useState(null);
  const [emailAction, setEmailAction] = useState('principale');
  const [savingEmail, setSavingEmail] = useState(false);
  const [enableInvoiceEmail, setEnableInvoiceEmail] = useState(true);
  const [syncingAttachments, setSyncingAttachments] = useState(false);

  // Stati per salvataggio allegato in anagrafica
  const [saveAttDialog, setSaveAttDialog] = useState({ open: false, att: null });
  const [saveAttDescription, setSaveAttDescription] = useState('');
  const [savingAtt, setSavingAtt] = useState(false);

  // Stati per AI Assistant
  const [aiPanelOpen, setAiPanelOpen] = useState(false);
  const [aiAnalysis, setAiAnalysis] = useState(null);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const { data: message, isLoading, error } = useQuery({
    queryKey: ['email', id],
    queryFn: () => api.get(`/emails/${id}`).then(res => res.data)
  });

  const flagMutation = useMutation({
    mutationFn: (is_flagged) => api.put(`/emails/${id}`, { is_flagged }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', id] });
      queryClient.invalidateQueries({ queryKey: ['emails'] });
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => api.delete(`/emails/${id}`),
    onSuccess: () => {
      navigate('/crm/email/inbox');
    }
  });

  // Assign customer mutation
  const assignCustomerMutation = useMutation({
    mutationFn: ({ emailId, customerId }) => emailsApi.assignCustomer(emailId, customerId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['email', id] });
      queryClient.invalidateQueries({ queryKey: ['emails'] });
      setAssignDialogOpen(false);
      setSuggestions([]);
    }
  });

  // Apri dialog associazione cliente
  const handleOpenAssignDialog = async () => {
    setAssignDialogOpen(true);
    setSuggestionsLoading(true);
    setSuggestions([]);
    setCustomerSearch('');
    
    try {
      const res = await emailsApi.suggestCustomers(id);
      setSuggestions(res.data?.suggestions || []);
    } catch (err) {
      console.error('Errore caricamento suggerimenti:', err);
    } finally {
      setSuggestionsLoading(false);
    }
  };

  // Rimuovi associazione cliente
  const handleRemoveAssociation = () => {
    if (window.confirm('Rimuovere l\'associazione con il cliente?')) {
      assignCustomerMutation.mutate({ emailId: id, customerId: null });
    }
  };

  const handleAddToBlacklist = async () => {
    const emailAddress = message?.data?.from_address;
    if (!emailAddress) {
      alert('Nessun indirizzo email da aggiungere');
      return;
    }
    
    if (window.confirm(`Aggiungere "${emailAddress}" alla blacklist?\nLe email da questo indirizzo non verranno più sincronizzate.`)) {
      try {
        await api.post('/company-settings/add-to-blacklist', { email: emailAddress });
        alert(`Email "${emailAddress}" aggiunta alla blacklist`);
      } catch (err) {
        alert('Errore: ' + (err.response?.data?.message || 'Impossibile aggiungere alla blacklist'));
      }
    }
  };

  // Associa cliente selezionato
  const handleAssignCustomer = async (customerId) => {
    if (!customerId) return;
    
    try {
      const res = await customersApi.getById(customerId);
      const customer = res.data?.data || res.data;
      setSelectedCustomerData(customer);
      
      if (!customer.email && !customer.email_secondaria) {
        setEmailAction('principale');
        setEnableInvoiceEmail(true);
      } else if (customer.email && !customer.email_secondaria) {
        setEmailAction('secondaria');
        setEnableInvoiceEmail(false);
      } else {
        setEmailAction('sostituisci_secondaria');
        setEnableInvoiceEmail(false);
      }
      
      setEmailDialogOpen(true);
    } catch (err) {
      console.error('Errore caricamento cliente:', err);
      assignCustomerMutation.mutate({ emailId: id, customerId });
    }
  };

  // Estrai indirizzo email dal mittente
  const getSenderEmail = () => {
    return message?.from_address || '';
  };

  // Salva email in anagrafica cliente e associa
  const handleSaveEmailAndAssign = async () => {
    if (!selectedCustomerData) return;
    
    setSavingEmail(true);
    const senderEmail = getSenderEmail();
    
    try {
      const updateData = {};
      
      if (emailAction === 'principale' || emailAction === 'sostituisci_principale') {
        updateData.email = senderEmail;
        if (enableInvoiceEmail) {
          updateData.invio_email = true;
        }
      } else if (emailAction === 'secondaria' || emailAction === 'sostituisci_secondaria') {
        updateData.email_secondaria = senderEmail;
      }
      
      await customersApi.update(selectedCustomerData.id, updateData);
      
      await assignCustomerMutation.mutateAsync({ 
        emailId: id, 
        customerId: selectedCustomerData.id 
      });
      
      setEmailDialogOpen(false);
      setAssignDialogOpen(false);
      setSelectedCustomerData(null);
      
    } catch (err) {
      console.error('Errore salvataggio email:', err);
      alert('Errore durante il salvataggio: ' + (err.response?.data?.error || err.message));
    } finally {
      setSavingEmail(false);
    }
  };

  // Associa senza salvare email in anagrafica
  const handleAssignWithoutEmail = () => {
    if (selectedCustomerData) {
      assignCustomerMutation.mutate({ 
        emailId: id, 
        customerId: selectedCustomerData.id 
      });
      setEmailDialogOpen(false);
      setSelectedCustomerData(null);
    }
  };

  // Cerca clienti per autocomplete
  const handleSearchCustomers = async (searchText) => {
    if (searchText.length < 2) {
      setCustomerOptions([]);
      return;
    }
    try {
      const res = await customersApi.getAll({ search: searchText, limit: 20 });
      setCustomerOptions(res.data?.data || []);
    } catch (err) {
      console.error('Errore ricerca clienti:', err);
    }
  };

  const handleDelete = () => {
    if (window.confirm('Eliminare questa email?')) {
      deleteMutation.mutate();
    }
  };

  // ============================================
  // AI ASSISTANT HANDLERS
  // ============================================
  
  // Carica analisi AI esistente
  const loadExistingAnalysis = async () => {
    try {
      const res = await aiMailApi.getAnalysis(id);
      if (res.data?.analyzed) {
        setAiAnalysis(res.data.analysis);
      }
    } catch (err) {
      console.log('Nessuna analisi AI esistente');
    }
  };

  // Analizza email con AI
  const handleAiAnalyze = async (model = null) => {
    console.log('🔍 [AI DEBUG] Starting handleAiAnalyze');
    console.log('🔍 [AI DEBUG] Email ID:', id, 'Type:', typeof id);
    console.log('🔍 [AI DEBUG] Model:', model);
    
    setAiLoading(true);
    setAiError(null);
    
    try {
      const requestPayload = { 
        model, 
        includeThread: true 
      };
      console.log('🔍 [AI DEBUG] Request payload:', JSON.stringify(requestPayload));
      
      const emailIdParsed = parseInt(id);
      console.log('🔍 [AI DEBUG] Parsed email ID:', emailIdParsed);
      
      console.log('🔍 [AI DEBUG] Calling aiMailApi.analyze...');
      const res = await aiMailApi.analyze(emailIdParsed, requestPayload);
      
      console.log('🔍 [AI DEBUG] Response received');
      console.log('🔍 [AI DEBUG] Response status:', res?.status);
      console.log('🔍 [AI DEBUG] Response data keys:', res?.data ? Object.keys(res.data) : 'no data');
      console.log('🔍 [AI DEBUG] Success:', res?.data?.success);
      
      if (res.data?.success) {
        console.log('🔍 [AI DEBUG] Analysis received, keys:', res.data.analysis ? Object.keys(res.data.analysis) : 'no analysis');
        setAiAnalysis(res.data.analysis);
        setSnackbar({ open: true, message: 'Analisi AI completata', severity: 'success' });
      } else {
        console.log('🔍 [AI DEBUG] Response not successful:', res.data?.error);
        throw new Error(res.data?.error || 'Errore analisi');
      }
    } catch (err) {
      console.error('🔍 [AI DEBUG] Error caught:', err);
      console.error('🔍 [AI DEBUG] Error name:', err?.name);
      console.error('🔍 [AI DEBUG] Error message:', err?.message);
      console.error('🔍 [AI DEBUG] Error stack:', err?.stack);
      console.error('🔍 [AI DEBUG] Response status:', err?.response?.status);
      console.error('🔍 [AI DEBUG] Response data:', err?.response?.data);
      
      const errorMsg = err.response?.data?.error || err.message || 'Errore durante l\'analisi AI';
      setAiError(errorMsg);
      setSnackbar({ open: true, message: errorMsg, severity: 'error' });
    } finally {
      console.log('🔍 [AI DEBUG] handleAiAnalyze finished');
      setAiLoading(false);
    }
  };

  // Rigenera analisi
  const handleAiRegenerate = () => {
    handleAiAnalyze();
  };

  // Usa modello avanzato
  const handleAiUpgradeModel = () => {
    handleAiAnalyze();
  };

  // Applica azione suggerita
  const handleAiApplyAction = async (action) => {
    try {
      const res = await aiMailApi.applyAction(parseInt(id), action);
      if (res.data?.success) {
        setSnackbar({ open: true, message: res.data.message || 'Azione applicata', severity: 'success' });
        queryClient.invalidateQueries({ queryKey: ['email', id] });
        queryClient.invalidateQueries({ queryKey: ['emails'] });
        return true;
      }
    } catch (err) {
      setSnackbar({ 
        open: true, 
        message: err.response?.data?.error || 'Errore applicazione azione', 
        severity: 'error' 
      });
      return false;
    }
  };

  // Inserisci bozza risposta
  const handleAiInsertDraft = (draft) => {
    // Naviga a compose con la bozza precompilata
    navigate(`/crm/email/compose?replyTo=${id}&aiDraft=${encodeURIComponent(draft)}`);
  };

  // Carica analisi esistente all'apertura del pannello
  const handleOpenAiPanel = () => {
    setAiPanelOpen(true);
    if (!aiAnalysis && !aiLoading) {
      loadExistingAnalysis();
    }
  };

  const handleSyncAttachments = async () => {
    if (!message?.account_id) return;
    try {
      setSyncingAttachments(true);
      await api.post(`/email-accounts/${message.account_id}/sync`);
      await queryClient.invalidateQueries({ queryKey: ['email', id] });
      await queryClient.invalidateQueries({ queryKey: ['emails'] });
    } catch (err) {
      console.error('Errore sync allegati:', err);
      alert('Errore durante la sincronizzazione degli allegati');
    } finally {
      setSyncingAttachments(false);
    }
  };

  const handleDownloadAttachment = async (attachmentId, filename) => {
    try {
      const response = await api.get(`/emails/${id}/attachments/${attachmentId}`, {
        responseType: 'blob'
      });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      link.setAttribute('download', filename || `allegato_${attachmentId}`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(blobUrl);
    } catch (err) {
      console.error('Errore download allegato:', err);
      alert('Errore nel download dell’allegato');
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">Errore caricamento email: {error.message}</Alert>
      </Box>
    );
  }

  if (!message) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Email non trovata</Alert>
      </Box>
    );
  }

  const parseAddresses = (raw) => {
    if (!raw) return [];
    if (Array.isArray(raw)) return raw;
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [parsed];
    } catch {
      return [raw];
    }
  };

  const toAddresses = [
    ...parseAddresses(message.to_addresses),
    ...[message.to_address, message.to_email, message.to].filter(Boolean)
  ];
  const fallbackRecipients = toAddresses.length > 0
    ? toAddresses
    : [message.customer?.email, message.customer?.email_secondaria].filter(Boolean);
  const ccAddresses = parseAddresses(message.cc_addresses);
  const isSent = message.folder === 'Sent' || message.folder === 'INBOX.Sent' || message.folder?.toLowerCase().includes('sent');
  const attachmentCount = message.attachments?.length ?? message.attachments_count ?? 0;

  // Helper per estrarre indirizzo email (gestisce sia stringhe che oggetti)
  const formatAddress = (addr) => {
    if (typeof addr === 'string') return addr;
    if (addr && addr.address) return addr.name ? `${addr.name} <${addr.address}>` : addr.address;
    return '';
  };

  return (
    <Box sx={{ p: { xs: 1, sm: 3 } }}>
      <Paper>
        <Toolbar sx={{ flexWrap: isMobile ? 'wrap' : 'nowrap', gap: isMobile ? 0.5 : 0 }}>
          <IconButton edge="start" onClick={() => navigate('/crm/email/inbox')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flexGrow: 1, ml: isMobile ? 0 : 2, fontSize: isMobile ? '0.9rem' : undefined, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {message.subject}
          </Typography>
          <IconButton onClick={() => flagMutation.mutate(!message.is_flagged)}>
            {message.is_flagged ? <StarIcon color="warning" /> : <StarBorderIcon />}
          </IconButton>
          
          {/* Pulsante Associa Cliente */}
          {message.customer ? (
            <Tooltip title="Rimuovi associazione cliente">
              <IconButton onClick={handleRemoveAssociation} color="primary">
                <LinkOffIcon />
              </IconButton>
            </Tooltip>
          ) : (
            <Tooltip title="Associa cliente">
              <IconButton onClick={handleOpenAssignDialog} color="primary">
                <PersonAddIcon />
              </IconButton>
            </Tooltip>
          )}
          
          <Button
            startIcon={<ReplyIcon />}
            onClick={() => navigate(`/crm/email/compose?replyTo=${message.id}`)}
            sx={{ mx: 1 }}
            size={isMobile ? 'small' : 'medium'}
          >
            {isMobile ? '' : 'Rispondi'}
          </Button>
          
          {/* Pulsante AI Assistant */}
          <Tooltip title="Analizza con AI">
            <Button
              variant={aiAnalysis ? 'outlined' : 'contained'}
              color="secondary"
              startIcon={aiLoading ? <CircularProgress size={16} color="inherit" /> : <AiIcon />}
              onClick={handleOpenAiPanel}
              disabled={aiLoading}
              sx={{ mx: 1 }}
            >
              {aiLoading ? 'Analisi...' : aiAnalysis ? 'AI' : 'Analizza'}
            </Button>
          </Tooltip>
          
          {/* Badge categoria AI se disponibile */}
          {aiAnalysis?.category && (
            <AiCategoryBadge
              category={aiAnalysis.category}
              confidence={aiAnalysis.confidence}
              rationale={aiAnalysis.rationale}
              size="small"
              onClick={handleOpenAiPanel}
            />
          )}
          
          <Tooltip title="Aggiungi mittente a blacklist">
            <IconButton onClick={handleAddToBlacklist} color="error">
              <BlockIcon />
            </IconButton>
          </Tooltip>
          <IconButton onClick={handleDelete} disabled={deleteMutation.isLoading}>
            <DeleteIcon />
          </IconButton>
        </Toolbar>

        <Divider />

        <Box sx={{ p: 3 }}>
          {/* Header Email */}
          <Box sx={{ mb: 3 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
              <Typography variant="h6" sx={{ flexGrow: 1 }}>
                {message.from_name || message.from_address}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {format(new Date(message.date_sent), "d MMMM yyyy 'alle' HH:mm", { locale: it })}
              </Typography>
            </Box>

            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
              <strong>Da:</strong> {message.from_address}
            </Typography>

            {(fallbackRecipients.length > 0 || isSent) && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>A:</strong> {fallbackRecipients.length > 0 ? fallbackRecipients.map(a => formatAddress(a)).join(', ') : '-'}
              </Typography>
            )}

            {ccAddresses.length > 0 && (
              <Typography variant="body2" color="text.secondary" sx={{ mb: 0.5 }}>
                <strong>Cc:</strong> {ccAddresses.map(a => formatAddress(a)).join(', ')}
              </Typography>
            )}

            {message.customer ? (
              <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip 
                  label={`Cliente: ${formatCustomerName(message.customer)}`} 
                  size="small" 
                  color="primary"
                  onClick={() => navigate(`/crm/clienti/${message.customer.id}`)}
                />
                <Tooltip title="Rimuovi associazione">
                  <IconButton size="small" onClick={handleRemoveAssociation}>
                    <LinkOffIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </Box>
            ) : (
              <Box sx={{ mt: 1 }}>
                <Button
                  size="small"
                  variant="outlined"
                  startIcon={<PersonAddIcon />}
                  onClick={handleOpenAssignDialog}
                >
                  Associa cliente
                </Button>
              </Box>
            )}
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Allegati */}
          {(message.has_attachments || attachmentCount > 0) && (
            <Card sx={{ mb: 3, bgcolor: 'grey.50' }}>
              <CardContent>
                <Typography variant="subtitle2" sx={{ mb: 1 }}>
                  <AttachFileIcon sx={{ fontSize: 18, verticalAlign: 'middle', mr: 0.5 }} />
                  Allegati ({attachmentCount})
                </Typography>
                {message.attachments && message.attachments.length > 0 ? (
                  <List dense>
                    {message.attachments.map((att) => (
                      <ListItem
                        key={att.id}
                        secondaryAction={
                          <Box sx={{ display: 'flex', gap: 0.5 }}>
                            {(message.customer_id || message.customer?.id) && (
                              <Tooltip title="Salva in anagrafica cliente">
                                <IconButton
                                  edge="end"
                                  color="primary"
                                  onClick={() => {
                                    const emailDate = message.date ? new Date(message.date).toLocaleDateString('it-IT') : new Date().toLocaleDateString('it-IT');
                                    setSaveAttDescription(`Allegato email del ${emailDate}`);
                                    setSaveAttDialog({ open: true, att });
                                  }}
                                >
                                  <SaveAltIcon />
                                </IconButton>
                              </Tooltip>
                            )}
                            <IconButton 
                              edge="end" 
                              onClick={() => handleDownloadAttachment(att.id, att.filename)}
                            >
                              <DownloadIcon />
                            </IconButton>
                          </Box>
                        }
                      >
                        <ListItemIcon>
                          <AttachFileIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={att.filename}
                          secondary={`${((att.size_bytes ?? att.size) / 1024).toFixed(1)} KB • ${att.content_type}`}
                        />
                      </ListItem>
                    ))}
                  </List>
                ) : (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Allegati presenti ma non ancora sincronizzati.
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      disabled={syncingAttachments}
                      onClick={handleSyncAttachments}
                    >
                      {syncingAttachments ? 'Sincronizzo...' : 'Risincronizza allegati'}
                    </Button>
                  </Box>
                )}
              </CardContent>
            </Card>
          )}

          {/* Dialog conferma salvataggio allegato in anagrafica */}
          <Dialog open={saveAttDialog.open} onClose={() => setSaveAttDialog({ open: false, att: null })} maxWidth="xs" fullWidth>
            <DialogTitle sx={{ fontSize: '0.95rem' }}>Salva in anagrafica cliente</DialogTitle>
            <DialogContent>
              <Typography variant="body2" sx={{ mb: 2 }}>
                Salvare <strong>{saveAttDialog.att?.filename}</strong> nei documenti del cliente?
              </Typography>
              <TextField
                fullWidth
                label="Descrizione"
                value={saveAttDescription}
                onChange={(e) => setSaveAttDescription(e.target.value)}
                size="small"
                autoFocus
              />
            </DialogContent>
            <DialogActions>
              <Button onClick={() => setSaveAttDialog({ open: false, att: null })} disabled={savingAtt}>Annulla</Button>
              <Button
                variant="contained"
                disabled={savingAtt}
                startIcon={savingAtt ? <CircularProgress size={16} /> : <SaveAltIcon />}
                onClick={async () => {
                  setSavingAtt(true);
                  try {
                    const custId = message.customer_id || message.customer?.id;
                    await customerDocumentsApi.saveFromEmail(custId, message.id, saveAttDialog.att.id, saveAttDescription);
                    setSnackbar({ open: true, message: 'Documento salvato in anagrafica cliente', severity: 'success' });
                    setSaveAttDialog({ open: false, att: null });
                  } catch (err) {
                    console.error('Errore salvataggio:', err);
                    setSnackbar({ open: true, message: 'Errore: ' + (err.response?.data?.message || err.message), severity: 'error' });
                  } finally {
                    setSavingAtt(false);
                  }
                }}
              >
                Salva
              </Button>
            </DialogActions>
          </Dialog>

          {/* Body Email */}
          <Box sx={{ mt: 3 }}>
            {message.body_html && message.body_html.length > 1 && message.body_html.includes('<') ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: 'background.paper',
                  '& img': { maxWidth: '100%' }
                }}
                dangerouslySetInnerHTML={{ __html: message.body_html }}
              />
            ) : message.body_text ? (
              <Box
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  p: 2,
                  backgroundColor: 'grey.50',
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'monospace'
                }}
              >
                {message.body_text}
              </Box>
            ) : (
              <Typography color="text.secondary" fontStyle="italic">
                (Nessun contenuto)
              </Typography>
            )}
          </Box>

          {/* Note interne */}
          {message.notes && (
            <Box sx={{ mt: 3, p: 2, bgcolor: 'warning.light', borderRadius: 1 }}>
              <Typography variant="subtitle2" sx={{ mb: 1 }}>Note interne:</Typography>
              <Typography variant="body2">{message.notes}</Typography>
            </Box>
          )}
        </Box>
      </Paper>

      {/* Dialog Associazione Cliente */}
      <Dialog 
        open={assignDialogOpen} 
        onClose={() => setAssignDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Associa Cliente all'Email
        </DialogTitle>
        <DialogContent>
          {message && (
            <Box sx={{ mb: 2, p: 1, bgcolor: 'grey.100', borderRadius: 1 }}>
              <Typography variant="subtitle2" color="text.secondary">
                Oggetto:
              </Typography>
              <Typography variant="body2" noWrap>
                {message.subject || '(Nessun oggetto)'}
              </Typography>
            </Box>
          )}

          {/* Ricerca manuale cliente */}
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            Cerca cliente
          </Typography>
          <TextField
            fullWidth
            variant="outlined"
            size="small"
            placeholder="Digita nome, codice cliente, ragione sociale..."
            value={customerSearch}
            onChange={(e) => {
              setCustomerSearch(e.target.value);
              handleSearchCustomers(e.target.value);
            }}
            InputProps={{
              startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
            }}
            sx={{ mb: 2 }}
          />
          
          {/* Risultati ricerca */}
          {customerSearch.length >= 2 && (
            <Box sx={{ maxHeight: 200, overflow: 'auto', mb: 2 }}>
              {customerOptions.length === 0 ? (
                <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
                  Nessun cliente trovato per "{customerSearch}"
                </Typography>
              ) : (
                <List dense>
                  {customerOptions.map((customer) => (
                    <ListItem key={customer.id} disablePadding>
                      <ListItemButton onClick={() => handleAssignCustomer(customer.id)}>
                        <ListItemIcon>
                          <PersonIcon />
                        </ListItemIcon>
                        <ListItemText
                          primary={
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <Chip label={customer.cod_cli} size="small" />
                              <Typography>
                                {customer.nome} {customer.cognome}
                              </Typography>
                            </Box>
                          }
                          secondary={customer.ragione_sociale || customer.email}
                        />
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              )}
            </Box>
          )}
          
          {customerSearch.length > 0 && customerSearch.length < 2 && (
            <Typography variant="caption" color="text.secondary" sx={{ mb: 2, display: 'block' }}>
              Digita almeno 2 caratteri per cercare...
            </Typography>
          )}

          <Divider sx={{ my: 2 }} />

          {/* Suggerimenti automatici */}
          {suggestionsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
              <CircularProgress size={24} />
              <Typography sx={{ ml: 2 }}>Analisi contenuto email...</Typography>
            </Box>
          ) : suggestions.length > 0 ? (
            <Box>
              <Typography variant="subtitle2" sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                <LightbulbIcon sx={{ mr: 1, color: 'warning.main' }} />
                Clienti suggeriti (match automatico)
              </Typography>
              <List dense>
                {suggestions.map((s) => (
                  <ListItem key={s.customer.id} disablePadding>
                    <ListItemButton onClick={() => handleAssignCustomer(s.customer.id)}>
                      <ListItemIcon>
                        <PersonIcon color="primary" />
                      </ListItemIcon>
                      <ListItemText
                        primary={
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Chip label={s.customer.cod_cli} size="small" color="primary" variant="outlined" />
                            <Typography>
                              {s.customer.nome} {s.customer.cognome}
                            </Typography>
                          </Box>
                        }
                        secondary={
                          <Box>
                            {s.customer.ragione_sociale && <Typography variant="caption">{s.customer.ragione_sociale}</Typography>}
                            <Typography variant="caption" color="success.main" sx={{ display: 'block' }}>
                              Match: {(s.reasons || s.matches || []).join(', ')}
                            </Typography>
                          </Box>
                        }
                      />
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
            </Box>
          ) : (
            <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 2 }}>
              Nessun suggerimento automatico trovato. Usa la ricerca sopra.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssignDialogOpen(false)}>
            Annulla
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog Gestione Email Anagrafica */}
      <Dialog 
        open={emailDialogOpen} 
        onClose={() => setEmailDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          Aggiungi Email in Anagrafica Cliente
        </DialogTitle>
        <DialogContent>
          {selectedCustomerData && (
            <>
              {/* Info cliente */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'primary.50', borderRadius: 1, border: '1px solid', borderColor: 'primary.200' }}>
                <Typography variant="subtitle2" color="primary.main" gutterBottom>
                  Cliente selezionato:
                </Typography>
                <Typography variant="h6">
                  {selectedCustomerData.nome} {selectedCustomerData.cognome}
                </Typography>
                {selectedCustomerData.ragione_sociale && (
                  <Typography variant="body2" color="text.secondary">
                    {selectedCustomerData.ragione_sociale}
                  </Typography>
                )}
                <Chip label={selectedCustomerData.cod_cli} size="small" sx={{ mt: 1 }} />
              </Box>

              {/* Email da aggiungere */}
              <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                  Indirizzo email da aggiungere:
                </Typography>
                <Typography variant="h6" color="primary">
                  {getSenderEmail()}
                </Typography>
              </Box>

              {/* Situazione attuale email */}
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Email attualmente in anagrafica:
                </Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, ml: 2 }}>
                  <Typography variant="body2">
                    <strong>Principale:</strong> {selectedCustomerData.email || <em style={{ color: '#999' }}>Non impostata</em>}
                    {selectedCustomerData.invio_email && selectedCustomerData.email && (
                      <Chip label="Invio fatture attivo" size="small" color="success" sx={{ ml: 1 }} />
                    )}
                  </Typography>
                  <Typography variant="body2">
                    <strong>Secondaria:</strong> {selectedCustomerData.email_secondaria || <em style={{ color: '#999' }}>Non impostata</em>}
                  </Typography>
                </Box>
              </Box>

              <Divider sx={{ my: 2 }} />

              {/* Scelta azione */}
              <FormControl component="fieldset" sx={{ width: '100%' }}>
                <FormLabel component="legend" sx={{ mb: 2 }}>
                  Come vuoi salvare questo indirizzo email?
                </FormLabel>
                <RadioGroup
                  value={emailAction}
                  onChange={(e) => setEmailAction(e.target.value)}
                >
                  {/* Caso 1: Nessuna email presente */}
                  {!selectedCustomerData.email && !selectedCustomerData.email_secondaria && (
                    <FormControlLabel 
                      value="principale" 
                      control={<Radio />} 
                      label={
                        <Box>
                          <Typography>Imposta come email principale</Typography>
                          <Typography variant="caption" color="text.secondary">
                            Sarà usata per l'invio delle fatture e comunicazioni
                          </Typography>
                        </Box>
                      }
                    />
                  )}

                  {/* Caso 2: Solo email principale presente */}
                  {selectedCustomerData.email && !selectedCustomerData.email_secondaria && (
                    <>
                      <FormControlLabel 
                        value="secondaria" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography>Aggiungi come email secondaria</Typography>
                            <Typography variant="caption" color="text.secondary">
                              L'email principale ({selectedCustomerData.email}) rimane invariata
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel 
                        value="sostituisci_principale" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography color="warning.main">Sostituisci email principale</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sovrascrive "{selectedCustomerData.email}"
                            </Typography>
                          </Box>
                        }
                      />
                    </>
                  )}

                  {/* Caso 3: Entrambe le email presenti */}
                  {selectedCustomerData.email && selectedCustomerData.email_secondaria && (
                    <>
                      <Alert severity="warning" sx={{ mb: 2 }}>
                        Il cliente ha già entrambi gli indirizzi email configurati. 
                        Seleziona quale vuoi sovrascrivere.
                      </Alert>
                      <FormControlLabel 
                        value="sostituisci_principale" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography color="warning.main">Sostituisci email principale</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sovrascrive "{selectedCustomerData.email}"
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel 
                        value="sostituisci_secondaria" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography color="warning.main">Sostituisci email secondaria</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sovrascrive "{selectedCustomerData.email_secondaria}"
                            </Typography>
                          </Box>
                        }
                      />
                    </>
                  )}

                  {/* Caso 4: Solo email secondaria presente (raro) */}
                  {!selectedCustomerData.email && selectedCustomerData.email_secondaria && (
                    <>
                      <FormControlLabel 
                        value="principale" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography>Imposta come email principale</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sarà usata per l'invio delle fatture e comunicazioni
                            </Typography>
                          </Box>
                        }
                      />
                      <FormControlLabel 
                        value="sostituisci_secondaria" 
                        control={<Radio />} 
                        label={
                          <Box>
                            <Typography color="warning.main">Sostituisci email secondaria</Typography>
                            <Typography variant="caption" color="text.secondary">
                              Sovrascrive "{selectedCustomerData.email_secondaria}"
                            </Typography>
                          </Box>
                        }
                      />
                    </>
                  )}
                </RadioGroup>
              </FormControl>

              {/* Checkbox invio fatture - solo se email principale */}
              {(emailAction === 'principale' || emailAction === 'sostituisci_principale') && (
                <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <FormControlLabel
                    control={
                      <Checkbox 
                        checked={enableInvoiceEmail} 
                        onChange={(e) => setEnableInvoiceEmail(e.target.checked)}
                      />
                    }
                    label={
                      <Box>
                        <Typography>Attiva invio fatture via email</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Le fatture verranno inviate automaticamente a questo indirizzo
                        </Typography>
                      </Box>
                    }
                  />
                </Box>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button 
            onClick={() => {
              setEmailDialogOpen(false);
              setSelectedCustomerData(null);
            }}
            disabled={savingEmail}
          >
            Annulla
          </Button>
          <Button 
            variant="outlined"
            onClick={handleAssignWithoutEmail}
            disabled={savingEmail}
          >
            Associa senza salvare email
          </Button>
          <Button 
            variant="contained"
            onClick={handleSaveEmailAndAssign}
            disabled={savingEmail}
          >
            {savingEmail ? <CircularProgress size={20} sx={{ mr: 1 }} /> : null}
            Salva e Associa
          </Button>
        </DialogActions>
      </Dialog>

      {/* AI Suggestion Panel */}
      <AiSuggestionPanel
        open={aiPanelOpen}
        onClose={() => setAiPanelOpen(false)}
        email={message}
        analysis={aiAnalysis}
        loading={aiLoading}
        error={aiError}
        onAnalyze={handleAiAnalyze}
        onApplyAction={handleAiApplyAction}
        onInsertDraft={handleAiInsertDraft}
        onRegenerate={handleAiRegenerate}
        onUpgradeModel={handleAiUpgradeModel}
      />

      {/* Snackbar per notifiche */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={() => setSnackbar({ ...snackbar, open: false })} 
          severity={snackbar.severity}
          variant="filled"
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}
